<script lang="ts">
export default {
  name: 'PromptPlaygroundPage',
};

interface ModelOption {
  value: string;
  label: string;
  description: string;
}
</script>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import Card from '@/components/ui/card/Card.vue';
import Badge from '@/components/ui/Badge.vue';
import Separator from '@/components/ui/Separator.vue';
import UISelect from '@/components/ui/UISelect.vue';
import DocumentSection from '@/components/ui/DocumentSection.vue';
import PlaygroundPromptSection from '@/components/ui/PlaygroundPromptSection.vue';
import ExecutionSection from '@/components/ui/ExecutionSection.vue';
import ResultsSection from '@/components/ui/ResultsSection.vue';
import PromptHistoryModal from '@/components/ui/PromptHistoryModal.vue';
import type { Prompt, ExecutionResult } from '@/types/ui/PromptManager';
import type { Document } from '@/types/ui/PromptManager';
import { aiService } from '@/services/AIService';
import { X } from 'lucide-vue-next';
import {
  InvokeModelRequest,
  InvokeModelRequestNova,
  InvokeModelRequestSonnet4,
  newInvokeModelRequestNova,
  newInvokeModelRequestSonnet4,
  ModelInvocationStatus,
} from '@/types/ai';
import { useModelInvocation } from '@/composables/useModelInvocation';

const selectedDocument = ref<Document | null>(null);
const selectedPrompt = ref<Prompt | null>(null);
const currentPromptContent = ref<string>('');
const currentSystemText = ref<string>('');
const currentExpectedOutput = ref<string>('');
const hasUnsavedChanges = ref(false);
const executionResult = ref<ExecutionResult | null>(null);
const isExecuting = ref(false);
const executionError = ref<string>('');
const selectedModel = ref<string>('');
const availableModels = ref<ModelOption[]>([]);
const historyModalRef = ref<InstanceType<typeof PromptHistoryModal> | null>(null);
const isTestMode = ref(false);
const currentInvocationId = ref<string | null>(null);
const invocationStatus = ref<ModelInvocationStatus | null>(null);

const { pollInvocationStatus } = useModelInvocation();

onMounted(async () => {
  availableModels.value = (await aiService.getAllModels()).map((model) => ({
    value: model.arn,
    label: model.name,
    description: model.description || 'No description available',
  }));
});

const canExecute = computed(() => (selectedModel.value && currentPromptContent.value && !isExecuting.value) || false);

const handleDocumentSelect = (document: Document | null) => {
  selectedDocument.value = document;
};

const handlePromptSelect = (prompt: Prompt) => {
  if (prompt.currentPromptText === undefined) {
    prompt.currentPromptText = prompt.userPrompt;
  }
  if (prompt.currentSystemText === undefined) {
    prompt.currentSystemText = prompt.systemPrompt;
  }
  if (prompt.currentExpectedOutput === undefined) {
    prompt.currentExpectedOutput = prompt.expectedOutputSchema;
  }

  selectedPrompt.value = prompt;
  currentPromptContent.value = prompt.currentPromptText || prompt.userPrompt || '';
  currentSystemText.value = prompt.currentSystemText || prompt.systemPrompt || '';
  currentExpectedOutput.value = prompt.currentExpectedOutput || prompt.expectedOutputSchema || '';
  isTestMode.value = false;
  hasUnsavedChanges.value =
    currentPromptContent.value !== (prompt.userPrompt || '') ||
    currentSystemText.value !== (prompt.systemPrompt || '');
};

const handlePromptContentChange = (content: string) => {
  currentPromptContent.value = content;
  if (selectedPrompt.value) {
    selectedPrompt.value.currentPromptText = content;
    hasUnsavedChanges.value =
      content !== (selectedPrompt.value.userPrompt || '') ||
      (selectedPrompt.value.currentSystemText || '') !== (selectedPrompt.value.systemPrompt || '');
  }
};

const handleSystemInstructionsChange = (content: string) => {
  currentSystemText.value = content;
  if (selectedPrompt.value && !isTestMode.value) {
    selectedPrompt.value.currentSystemText = content;
    hasUnsavedChanges.value =
      currentPromptContent.value !== (selectedPrompt.value.userPrompt || '') ||
      content !== (selectedPrompt.value.systemPrompt || '');
  }
};

const handleTestPromptChange = (systemText: string, promptText: string) => {
  currentSystemText.value = systemText;
  currentPromptContent.value = promptText;
  currentExpectedOutput.value = ''; // Clear expected output for test prompts
  isTestMode.value = true;
  selectedPrompt.value = null;
  hasUnsavedChanges.value = false;
};

function handlePromptReset(prompt: Prompt) {
  currentPromptContent.value = prompt.userPrompt || '';
  currentExpectedOutput.value = prompt.expectedOutputSchema || '';
  if (selectedPrompt.value) {
    selectedPrompt.value.currentPromptText = prompt.userPrompt || '';
    selectedPrompt.value.currentSystemText = prompt.systemPrompt || '';
    selectedPrompt.value.currentExpectedOutput = prompt.expectedOutputSchema || '';
  }
  hasUnsavedChanges.value = false;
}


const handleExecute = async () => {
  if (!selectedDocument.value || !currentPromptContent.value) return;

  // Clear previous error and result
  executionError.value = '';
  executionResult.value = null;
  currentInvocationId.value = null;
  invocationStatus.value = null;

  try {
    // construct payload
    let request: InvokeModelRequestNova | InvokeModelRequestSonnet4 | undefined;
    const selectedModelLabel = availableModels.value.find((m) => m.value === selectedModel.value)?.label ?? '';

    if (selectedModelLabel.toLowerCase().includes('nova')) {
      request = newInvokeModelRequestNova();
      request.messages.push({
        role: 'user',
        content: [{ text: currentPromptContent.value }],
      });
      request.system = [
        {
          text: currentSystemText.value || '',
        },
      ];
    } else if (selectedModelLabel.toLowerCase().includes('sonnet')) {
      request = newInvokeModelRequestSonnet4();
      request.messages = [
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: currentPromptContent.value,
            },
          ],
        },
      ];
      request.system = [
        {
          type: 'text',
          text: currentSystemText.value || '',
        },
      ];
    }

    isExecuting.value = true;
    invocationStatus.value = 'PENDING';

    // Invoke model asynchronously
    // For playground documents, use s3Key; for platform documents, use documentId
    const asyncResponse = await aiService.invokeModelAsync({
      modelId: selectedModel.value,
      payload: request,
      documentId: selectedDocument.value.documentId,
      s3Key: selectedDocument.value.s3Key,
      expectedOutputSchema: currentExpectedOutput.value,
      promptId: selectedPrompt.value ? Number(selectedPrompt.value.promptId) : undefined,
      promptVersion: selectedPrompt.value?.version,
    } as InvokeModelRequest);

    currentInvocationId.value = asyncResponse.invocationId;
    invocationStatus.value = 'PENDING';

    // Start polling for status
    pollInvocationStatus(
      asyncResponse.invocationId,
      async (statusResponse) => {
        if (statusResponse.status === 'COMPLETED') {
          // Get the result
          try {
            const result = await aiService.getInvocationResult(asyncResponse.invocationId);
            executionResult.value = {
              output: result.rawOutput,
            };
            invocationStatus.value = 'COMPLETED';
            isExecuting.value = false;
          } catch (error: any) {
            executionError.value = error.message || 'Failed to retrieve result';
            invocationStatus.value = 'FAILED';
            isExecuting.value = false;
          }
        } else if (statusResponse.status === 'FAILED') {
          executionError.value = statusResponse.errorMessage || 'Model invocation failed';
          invocationStatus.value = 'FAILED';
          isExecuting.value = false;
        }
      },
      (error) => {
        executionError.value = error.message || 'Failed to poll invocation status';
        invocationStatus.value = 'FAILED';
        isExecuting.value = false;
      },
      (statusResponse) => {
        // Update status as it changes
        invocationStatus.value = statusResponse.status;
      }
    );
  } catch (error: any) {
    isExecuting.value = false;
    invocationStatus.value = 'FAILED';
    executionError.value = error.message || 'An error occurred while executing the prompt. Please try again.';
    console.error('Execution error:', error);
  }
};

function formatVersion(version: string) {
  if (version.toLowerCase() === 'draft') {
    return 'DRAFT';
  } else {
    return `v${version}`;
  }
}

const handleShowHistory = () => {
  historyModalRef.value?.show();
};
</script>

<template>
  <div class="tw:flex tw:flex-col tw:gap-4">
    <!-- Header -->
    <div class="tw:space-y-2 tw:text-center">
      <h1 class="tw:bg-clip-text tw:text-3xl tw:font-bold">🧠 AI Extraction Playground</h1>
      <p class="tw:text-md tw:mx-auto tw:max-w-2xl tw:text-slate-600">
        Test utilizing Large Language Models to extract data from documents
      </p>
    </div>

    <!-- Status Bar -->
    <Card class="tw:border-slate-200 tw:bg-white/80 tw:p-4 tw:backdrop-blur-sm">
      <div class="tw:flex tw:items-center tw:justify-between">
        <div class="tw:flex tw:items-center tw:gap-4">
          <div class="tw:flex tw:items-center tw:gap-2">
            <span class="tw:text-md tw:font-bold tw:text-slate-600">Document Process:</span>
          </div>
          <Separator orientation="vertical" class="tw:h-6" />
          <div class="tw:flex tw:items-center tw:gap-2">
            <span class="tw:text-sm tw:font-medium tw:text-slate-600">Document:</span>
            <div v-if="selectedDocument" class="tw:relative tw:inline-flex">
              <Badge variant="secondary" class="tw:pr-2">
                <span class="tw:max-w-[200px] tw:truncate">{{ selectedDocument.displayName || selectedDocument.name }}</span>
              </Badge>
              <button
                @click="selectedDocument = null"
                class="tw:absolute tw:-right-2 tw:-top-2 tw:flex tw:h-4 tw:w-4 tw:items-center tw:justify-center tw:rounded-full tw:bg-base-200 tw:text-red-500 tw:opacity-80 tw:transition-all tw:hover:text-red-600 tw:hover:opacity-100"
                title="Unselect document">
                <X class="tw:h-4 tw:w-4 tw:transition-all tw:hover:font-bold" />
              </button>
            </div>
            <Badge v-else variant="outline">None selected</Badge>
          </div>
          <Separator orientation="vertical" class="tw:h-6" />
          <div class="tw:flex tw:items-center tw:gap-2">
            <span class="tw:text-sm tw:font-medium tw:text-slate-600">Prompt:</span>
            <div v-if="isTestMode" class="tw:flex tw:items-center tw:gap-2">
              <div class="tw:relative tw:inline-flex">
                <Badge variant="secondary" class="tw:pr-2 tw:bg-purple-100 tw:text-purple-800">
                  Test Prompt
                </Badge>
                <button
                  @click="
                    isTestMode = false;
                    currentPromptContent = '';
                    currentSystemText = '';
                  "
                  class="tw:absolute tw:-right-2 tw:-top-2 tw:flex tw:h-4 tw:w-4 tw:items-center tw:justify-center tw:rounded-full tw:bg-base-200 tw:text-red-500 tw:opacity-80 tw:transition-all tw:hover:text-red-600 tw:hover:opacity-100"
                  title="Clear test prompt">
                  <X class="tw:h-4 tw:w-4 tw:transition-all tw:hover:font-bold" />
                </button>
              </div>
            </div>
            <div v-else-if="selectedPrompt" class="tw:flex tw:items-center tw:gap-2">
              <div class="tw:relative tw:inline-flex">
                <Badge variant="secondary" class="tw:pr-2">
                  {{ selectedPrompt.name }} ({{ formatVersion(selectedPrompt.version) }})
                  <span v-if="currentExpectedOutput" class="tw:ml-2 tw:text-xs tw:bg-green-100 tw:text-green-700 tw:px-1 tw:py-0.5 tw:rounded" title="This prompt has an expected output schema">
                    📋 Schema
                  </span>
                </Badge>
                <button
                  @click="
                    selectedPrompt = null;
                    currentPromptContent = '';
                    currentSystemText = '';
                    currentExpectedOutput = '';
                    hasUnsavedChanges = false;
                  "
                  class="tw:absolute tw:-right-2 tw:-top-2 tw:flex tw:h-4 tw:w-4 tw:items-center tw:justify-center tw:rounded-full tw:bg-base-200 tw:text-red-500 tw:opacity-80 tw:transition-all tw:hover:text-red-600 tw:hover:opacity-100"
                  title="Unselect prompt">
                  <X class="tw:h-4 tw:w-4 tw:transition-all tw:hover:font-bold" />
                </button>
              </div>
              <span v-if="hasUnsavedChanges" class="tw:relative tw:flex tw:h-2 tw:w-2" title="Unsaved changes">
                <span
                  class="tw:absolute tw:inline-flex tw:h-full tw:w-full tw:animate-pulse tw:rounded-full tw:bg-amber-400 tw:opacity-30"></span>
                <span class="tw:relative tw:inline-flex tw:h-2 tw:w-2 tw:rounded-full tw:bg-amber-500"></span>
              </span>
            </div>
            <Badge v-else variant="outline">None selected</Badge>
          </div>
          <Separator orientation="vertical" class="tw:h-6" />
          <div class="tw:flex tw:items-center tw:gap-2">
            <span class="tw:text-sm tw:font-medium tw:text-slate-600">Model:</span>
            <div v-if="selectedModel" class="tw:relative tw:inline-flex">
              <Badge variant="secondary" class="tw:pr-2">
                {{ availableModels.find((m) => m.value === selectedModel)?.label || 'Unknown' }}
              </Badge>
              <button
                @click="selectedModel = ''"
                class="tw:absolute tw:-right-2 tw:-top-2 tw:flex tw:h-4 tw:w-4 tw:items-center tw:justify-center tw:rounded-full tw:bg-base-200 tw:text-red-500 tw:opacity-80 tw:transition-all tw:hover:text-red-600 tw:hover:opacity-100"
                title="Unselect model">
                <X class="tw:h-4 tw:w-4 tw:transition-all tw:hover:font-bold" />
              </button>
            </div>
            <Badge v-else variant="outline">None selected</Badge>
          </div>
        </div>
      </div>
    </Card>

    <!-- Model Configuration Section -->
    <Card class="tw:border-slate-200 tw:bg-white/80 tw:p-6 tw:backdrop-blur-sm">
      <div class="tw:space-y-4">
        <div>
          <h2 class="tw:mb-2 tw:text-lg tw:font-semibold">Model Configuration</h2>
          <p class="tw:text-sm tw:text-slate-600">Select the AWS Bedrock model to use for document processing</p>
        </div>
        <div class="tw:max-w-md">
          <UISelect v-model="selectedModel" :options="availableModels" placeholder="Select a model" />
        </div>
      </div>
    </Card>

    <!-- Main Content Grid -->
    <div class="tw:grid tw:grid-cols-1 tw:gap-6 tw:lg:grid-cols-2">
      <!-- Left Column -->
      <div class="tw:space-y-6">
        <DocumentSection :selectedDocument="selectedDocument" @documentSelect="handleDocumentSelect" />
        <ExecutionSection
          :canExecute="canExecute"
          :isExecuting="isExecuting"
          @execute="handleExecute"
          :selectedDocument="selectedDocument"
          :selectedModel="selectedModel"
          :promptContent="currentPromptContent"
          :error="executionError"
          :invocationId="currentInvocationId"
          :invocationStatus="invocationStatus" />
      </div>

      <!-- Right Column -->
      <div class="tw:space-y-6">
        <PlaygroundPromptSection
          :selectedPrompt="selectedPrompt"
          :currentContent="currentPromptContent"
          :hasUnsavedChanges="hasUnsavedChanges"
          :expectedOutputSchema="currentExpectedOutput"
          @promptSelect="handlePromptSelect"
          @contentChange="handlePromptContentChange"
          @promptReset="handlePromptReset"
          @systemInstructionsChange="handleSystemInstructionsChange"
          @testPromptChange="handleTestPromptChange" />
        <ResultsSection
          :result="executionResult"
          @clear="executionResult = null"
          @show-history="handleShowHistory" />
      </div>
    </div>
    
    <!-- Prompt History Modal -->
    <PromptHistoryModal ref="historyModalRef" />
  </div>


</template>
